// What the console shows when it breaks.
//
// The stakes here are different from the customer's page. A dispatcher is
// mid-round with an operator's whole customer book in this tab's storage,
// and a blank screen reads as "I have lost the day's work". The reassuring
// part is true and worth saying: nothing is lost, because the jobs are on
// disk in this browser rather than in the component that just failed.
//
// The scrubbing rule still applies and matters more, not less. The
// console holds sealed tickets and tracking links for *every* stop on a
// round, so an unscrubbed error message here could carry several
// customers' capabilities rather than one. `safeErrorText` lives in
// `packages/core` where it is tested; this component must not route
// around it by rendering `error.stack` or `location.href` for "context".

import { Component, type ErrorInfo, type ReactNode } from "react";
import { safeErrorText } from "@wayfind/core";

interface State {
  failed: boolean;
  detail: string | null;
}

export class Boundary extends Component<{ children: ReactNode }, State> {
  state: State = { failed: false, detail: null };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, detail: safeErrorText(error) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The console is a developer's, not a reporter's — nothing leaves the
    // device here, and nothing should start to without the scrubbing
    // above coming with it.
    console.error("wayfind: render failed", safeErrorText(error), info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="shell">
        <div className="shell__inner">
          <section className="panel">
            <p className="eyebrow">Something went wrong in the console</p>
            <p style={{ margin: "0 0 0.75rem" }}>
              Your rounds are safe — they are stored in this browser, not in the
              part that failed. Reloading brings them back. Nothing about this
              has been sent anywhere.
            </p>
            <button type="button" className="ghost-button" onClick={() => location.reload()}>
              Try again
            </button>
            {this.state.detail ? (
              <p className="field__hint" style={{ marginTop: "0.75rem" }}>
                {this.state.detail}
              </p>
            ) : null}
          </section>
          <footer className="footer">
            <p>Jobs, devices and routes are held in this browser's own storage.</p>
          </footer>
        </div>
      </div>
    );
  }
}
